import {createReducer, on} from '@ngrx/store';
import {BuildingDetails} from '@shared/models/building-details';
import {
    loadBuilding,
    loadBuildingFailure,
    loadBuildingSuccess,
    loadLocation,
    loadLocationFailure,
    loadLocationSuccess,
    loadMeter,
    loadMeterFailure,
    loadMeterSuccess,
    loadUser,
    loadUserFailure,
    loadUserSuccess,
    logOut,
} from 'src/app/state/actions';
import {MeterData} from 'src/app/meter/meter-data/meter-data.model';
import {User} from '@shared/services/user.service';
import {Location} from '@shared/services/location.service';

export type StateError = {
    status: 'error';
};

export type StatePending = {
    status: 'pending';
};

export type StateLoading = {
    status: 'loading';
};

export type BuildingStateSuccess = {
    buildingNo: string;
    buildingDetails: BuildingDetails;
    status: 'success';
};

export type MeterStateSuccess = {
    meter: MeterData;
    status: 'success';
};

export type UserStateSuccess = {
    user: User;
    status: 'success';
};

export type LocationStateSuccess = {
    location: Location;
    status: 'success';
};

export type BuildingState = BuildingStateSuccess | StateError | StatePending | StateLoading;
export type MeterState = MeterStateSuccess | StateError | StatePending | StateLoading;
export type UserState = UserStateSuccess | StateError | StatePending | StateLoading;
export type LocationState = LocationStateSuccess | StateError | StatePending | StateLoading;

export const buildingReducer = createReducer<BuildingState>(
    {status: 'pending'},
    on(loadBuilding, () => ({
        status: 'loading',
    })),
    on(loadBuildingSuccess, (_, {building: buildingDetails}) => ({
        buildingNo: buildingDetails.building.buildingNo,
        buildingDetails: buildingDetails,
        status: 'success',
    })),
    on(loadBuildingFailure, (_, {error}) => {
        console.error(error);
        return {
            status: 'error',
        };
    }),
);

export const userReducer = createReducer<UserState>(
    {status: 'pending'},
    // Keep an already-loaded profile - every sign-in screen dispatches loadUser.
    on(loadUser, state => (state.status === 'success' ? state : {status: 'loading'})),
    on(loadUserSuccess, (_, {user: user}) => ({
        user: user,
        status: 'success',
    })),
    on(loadUserFailure, (_, {error}) => {
        console.error(error);
        return {
            status: 'error',
        };
    }),
    on(logOut, () => ({status: 'pending'})),
);

export const meterReducer = createReducer<MeterState>(
    {status: 'pending'},
    on(loadMeter, () => ({
        status: 'loading',
    })),
    on(loadMeterSuccess, (_, {meter: meter}) => ({
        meter: meter,
        status: 'success',
    })),
    on(loadMeterFailure, (_, {error}) => {
        console.error(error);
        return {
            status: 'error',
        };
    }),
);

export const locationReducer = createReducer<LocationState>(
    {status: 'pending'},
    on(loadLocation, () => ({
        status: 'loading',
    })),
    on(loadLocationSuccess, (_, {location: location}) => ({
        location: location,
        status: 'success',
    })),
    on(loadLocationFailure, (_, {error}) => {
        console.error(error);
        return {
            status: 'error',
        };
    }),
);
